import type { ReactNode } from 'react';
import { createElement } from 'react';
import {
  CloseCircleOutlined,
  GiftOutlined,
  InboxOutlined,
  InfoCircleOutlined,
  MailOutlined,
  MessageOutlined,
  SafetyCertificateOutlined,
  ShoppingCartOutlined,
  SolutionOutlined,
  StopOutlined,
  TrophyOutlined,
} from '@ant-design/icons';
import type { BadgeProps } from '@shared/ui';
import type { NotificationType } from '@entities/notification';

const TYPE_TONE: Record<NotificationType, NonNullable<BadgeProps['tone']>> = {
  WELCOME: 'primary',
  EMAIL_VERIFIED: 'success',
  USER_BLOCKED: 'danger',
  KYC_APPROVED: 'success',
  KYC_REJECTED: 'danger',
  RFQ_RECEIVED: 'info',
  QUOTE_RECEIVED: 'info',
  QUOTE_ACCEPTED: 'success',
  ORDER_STATUS_CHANGED: 'warning',
  CHAT_MESSAGE: 'info',
  SYSTEM: 'neutral',
};

export interface NotificationVisual {
  icon: ReactNode;
  /** Solid accent for the icon glyph and the unread rail. */
  accent: string;
  /** Same hue as `accent`, as "r,g,b" — feeds the card's gradient wash. */
  rgb: string;
}

/*
 * Hues are borrowed from the surfaces these notifications point at, so the
 * colour itself hints at the destination: RFQ blue matches the CRM "new"
 * column, quotes violet matches "quoted", orders amber matches the order
 * chips. Icons reuse the app's existing vocabulary for the same reason.
 */
const TYPE_VISUAL: Record<NotificationType, NotificationVisual> = {
  WELCOME: { icon: createElement(GiftOutlined), accent: '#2f5cf6', rgb: '47,92,246' },
  EMAIL_VERIFIED: { icon: createElement(MailOutlined), accent: '#10b981', rgb: '16,185,129' },
  USER_BLOCKED: { icon: createElement(StopOutlined), accent: '#e5484d', rgb: '229,72,77' },
  KYC_APPROVED: {
    icon: createElement(SafetyCertificateOutlined),
    accent: '#10b981',
    rgb: '16,185,129',
  },
  KYC_REJECTED: { icon: createElement(CloseCircleOutlined), accent: '#e5484d', rgb: '229,72,77' },
  RFQ_RECEIVED: { icon: createElement(InboxOutlined), accent: '#2f5cf6', rgb: '47,92,246' },
  QUOTE_RECEIVED: { icon: createElement(SolutionOutlined), accent: '#8b5cf6', rgb: '139,92,246' },
  QUOTE_ACCEPTED: { icon: createElement(TrophyOutlined), accent: '#10b981', rgb: '16,185,129' },
  ORDER_STATUS_CHANGED: {
    icon: createElement(ShoppingCartOutlined),
    accent: '#d97706',
    rgb: '245,158,11',
  },
  CHAT_MESSAGE: { icon: createElement(MessageOutlined), accent: '#0ea5e9', rgb: '14,165,233' },
  SYSTEM: { icon: createElement(InfoCircleOutlined), accent: '#6b7684', rgb: '107,118,132' },
};

const FALLBACK: NotificationVisual = TYPE_VISUAL.SYSTEM;

export function notificationTone(type: NotificationType): NonNullable<BadgeProps['tone']> {
  return TYPE_TONE[type] ?? 'neutral';
}

export function notificationVisual(type: NotificationType): NotificationVisual {
  return TYPE_VISUAL[type] ?? FALLBACK;
}

/** i18n key for the badge label — consumers call t() with the resulting key. */
export function notificationLabelKey(type: NotificationType): string {
  return `notifications.types.${type}`;
}

/**
 * Compact timestamp: time of day for today, day + month otherwise. The full
 * `toLocaleString()` output is too long to sit beside a title without wrapping.
 */
export function formatNotificationTime(iso: string, locale: string): string {
  const date = new Date(iso);
  if (Number.isNaN(date.getTime())) return '';
  const now = new Date();
  const sameDay =
    date.getDate() === now.getDate() &&
    date.getMonth() === now.getMonth() &&
    date.getFullYear() === now.getFullYear();

  if (sameDay) {
    return date.toLocaleTimeString(locale, { hour: '2-digit', minute: '2-digit' });
  }
  return date.toLocaleDateString(locale, {
    day: 'numeric',
    month: 'short',
    ...(date.getFullYear() === now.getFullYear() ? {} : { year: 'numeric' }),
  });
}
